import type { Ari } from '@atlassian/ari';
import { AnyAri } from '@atlassian/ari/any-ari';
import { CanvasBoardAri } from '@atlassian/ari/canvas/board';
import { z } from 'zod';

import { BaseError, type BaseErrorOptions, createErrorClass } from '@canvas/error';

/**
 * The allowed document types
 */
export const allowedDocumentTypes = ['whiteboard', 'database'] as const;
export type DocumentType = (typeof allowedDocumentTypes)[number];

export function isAllowedDocumentType(type: string): type is DocumentType {
  return allowedDocumentTypes.includes(type as DocumentType);
}

export function assertIsAllowedDocumentType(type: string): asserts type is DocumentType {
  if (!isAllowedDocumentType(type)) {
    throw new InvalidDocumentTypeError(type);
  }
}

export class InvalidDocumentTypeError extends createErrorClass(
  'InvalidDocumentTypeError'
) {
  constructor(documentType: string, options?: BaseErrorOptions) {
    super(`Unknown document type: ${documentType}`, options);
  }
}

const validator = z.object({
  resourceType: z.enum([...allowedDocumentTypes, 'board']),
  resourceId: z.string().uuid(),
  cloudId: z.string().nonempty(),
  resourceOwner: z.literal('canvas'),
});

function formatError(error: z.ZodError): string {
  return error.errors.map((e) => `${e.path.join('.')}: ${e.message}`).join(', ');
}

/**
 * An error thrown when a `CanvasDocumentId` fails validation
 */
export class InvalidCanvasDocumentIdError extends BaseError {
  constructor(validationError: string, options?: BaseErrorOptions) {
    super(
      { name: 'InvalidCanvasDocumentIdError', ...options },
      `Document id failed validation - ${validationError}`
    );
  }
}

/**
 * A Canvas document id
 *
 * This is intended for use within the canvas codebase,
 * rather than relying upon ARI types internally.
 */
export class CanvasDocumentId {
  /**
   * The document id
   */
  public readonly documentId: string;

  /**
   * The cloud id this document is associated with
   */
  public readonly cloudId: string;

  /**
   * The document type
   */
  public readonly documentType: DocumentType;

  /**
   * @deprecated Use `documentId` instead, this is included to make the `CanvasDocumentId` compatible with `CanvasBoardAri`
   *
   * @see {@link CanvasDocumentId.documentId}
   */
  get boardId(): string {
    return this.documentId;
  }

  /**
   * @deprecated Use `documentId` instead, this is included to make the `CanvasDocumentId` compatible with `CanvasBoardAri`
   *
   * @see {@link CanvasDocumentId.documentId}
   */
  get resourceId(): string {
    return this.documentId;
  }

  /**
   * @deprecated Use `documentId` instead, this is included to make the `CanvasDocumentId` compatible with `CanvasBoardAri`
   *
   * @see {@link CanvasDocumentId.cloudId}
   */
  get siteId(): string {
    return this.cloudId;
  }

  private constructor(documentId: string, cloudId: string, documentType: DocumentType) {
    this.documentId = documentId;
    this.cloudId = cloudId;
    this.documentType = documentType;
  }

  /**
   * Create a `CanvasDocumentId` from an `Ari`
   * @param ari
   */
  static fromAri(ari: AnyAri): CanvasDocumentId {
    const result = validator.safeParse(ari);

    if (!result.success) {
      throw new InvalidCanvasDocumentIdError(formatError(result.error));
    }

    const resourceType =
      result.data.resourceType === 'board' ? 'whiteboard' : result.data.resourceType;

    return new CanvasDocumentId(
      result.data.resourceId,
      result.data.cloudId,
      resourceType
    );
  }

  /**
   * Parse an ARI string into a `CanvasDocumentId`
   * @param ari
   */
  static parse(ari: string): CanvasDocumentId {
    let parsed: AnyAri;
    try {
      parsed = AnyAri.parse(ari);
    } catch (err: any) {
      throw new InvalidCanvasDocumentIdError(err.message);
    }
    return CanvasDocumentId.fromAri(parsed);
  }

  /**
   * Create a new `CanvasDocumentId`
   * @param documentType The document type to use (defaults to `whiteboard`)
   * @param cloudId The site id / cloud id
   * @param documentId The document id
   */
  static create({
    documentType = 'whiteboard',
    cloudId,
    documentId,
  }: {
    documentType?: DocumentType;
    cloudId: string;
    documentId: string;
  }): CanvasDocumentId {
    return new CanvasDocumentId(documentId, cloudId, documentType);
  }

  /**
   * Convert to JSON
   *
   * Transforms the id into an ARI string
   */
  toJSON() {
    return this.toAri().toJSON();
  }

  /**
   * Convert to a string
   */
  toString() {
    return this.toAri().toString();
  }

  /**
   * Convert to an `Ari` object
   */
  toAri(): Ari {
    switch (this.documentType) {
      case 'whiteboard':
        return CanvasBoardAri.create({
          siteId: this.cloudId,
          boardId: this.documentId,
        });
      default:
        return AnyAri.create({
          resourceType: this.documentType,
          resourceId: this.documentId,
          cloudId: this.cloudId,
          resourceOwner: 'canvas',
        });
    }
  }
}
